import React from 'react';
import { Helmet } from 'react-helmet';
import './Compute.css';

const Compute = () => {
    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Cloud VPS Hosting - High-Performance Virtual Servers | Epicfinal</title>
                <meta name="title" content="Cloud VPS Hosting - High-Performance Virtual Servers | Epicfinal" />
                <meta name="description" content="Scalable VPS hosting from $64.90/mo. NVMe SSD, up to 35GB RAM, 16 vCPUs, full root access, DDoS protection & 99.9% uptime. Perfect for SaaS, e-commerce & production workloads." />
                <meta name="keywords" content="VPS hosting, cloud VPS, virtual private server, cloud compute, NVMe VPS, SSD VPS, managed VPS, scalable VPS, VPS server, cloud server, dedicated resources, Linux VPS, root access VPS, production hosting" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/compute" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/compute" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/compute" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/compute" />
                <meta property="og:title" content="Cloud VPS Hosting - High-Performance Virtual Servers | Epicfinal" />
                <meta property="og:description" content="Scalable VPS hosting from $64.90/mo. NVMe SSD, dedicated resources, full root access & 99.9% uptime SLA." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Cloud VPS - High-Performance Virtual Servers" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/compute" />
                <meta name="twitter:title" content="Cloud VPS Hosting - Scalable Virtual Servers | Epicfinal" />
                <meta name="twitter:description" content="Scalable VPS from $64.90/mo. NVMe SSD, up to 35GB RAM, DDoS protection & 99.9% uptime." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Cloud VPS Hosting - High-Performance Virtual Servers",
                        "description": "Scalable VPS hosting with NVMe SSD storage, dedicated resources, full root access, and 99.9% uptime guarantee.",
                        "url": "https://epicfinal.com/compute",
                        "provider": {
                            "@type": "Organization",
                            "name": "Epicfinal",
                            "url": "https://epicfinal.com"
                        },
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://epicfinal.com"},
                                {"@type": "ListItem", "position": 2, "name": "Compute VPS", "item": "https://epicfinal.com/compute"}
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Epicfinal Cloud VPS",
                        "description": "High-performance VPS hosting with NVMe SSD storage, dedicated vCPUs, and full root access",
                        "brand": {"@type": "Brand", "name": "Epicfinal"},
                        "category": "Cloud Computing",
                        "offers": [
                            {"@type": "Offer", "name": "VPS-A Core", "price": "64.90", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock", "url": "https://my.epicfinal.com/index.php?rp=/store/compute/vps1"},
                            {"@type": "Offer", "name": "VPS-B Edge", "price": "168.90", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock", "url": "https://my.epicfinal.com/index.php?rp=/store/compute/vm-edge"},
                            {"@type": "Offer", "name": "VPS-C MTI-L", "price": "270.90", "priceCurrency": "USD", "priceValidUntil": "2025-12-31", "availability": "https://schema.org/InStock", "url": "https://my.epicfinal.com/index.php?rp=/store/compute/mti-l"}
                        ],
                        "aggregateRating": {"@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "95", "bestRating": "5", "worstRating": "1"}
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {"@type": "Question", "name": "What is VPS hosting?", "acceptedAnswer": {"@type": "Answer", "text": "VPS (Virtual Private Server) hosting provides dedicated resources on a virtualized server. Unlike shared hosting, you get guaranteed CPU, RAM, and storage with full root access for complete control."}},
                            {"@type": "Question", "name": "What VPS plans does Epicfinal offer?", "acceptedAnswer": {"@type": "Answer", "text": "We offer three VPS tiers: Core (4 vCPU, 8GB RAM, $64.90/mo), Edge (8 vCPU, 16GB RAM, $168.90/mo), and MTI-L (16 vCPU, 35GB RAM, $269.90/mo). All include NVMe SSD storage."}},
                            {"@type": "Question", "name": "Can I add managed services to my VPS?", "acceptedAnswer": {"@type": "Answer", "text": "Yes, full management is available as an add-on including 24/7 monitoring, security updates, remote backups, and priority technical support."}}
                        ]
                    }
                `}</script>
            </Helmet>

            <div className="compute-page">
                {/* Hero Section */}
                <section className="compute-hero">
                    <div className="container">
                        <div className="compute-hero-content">
                            <h1 className="compute-hero-title">
                                Cloud Compute VPS
                            </h1>
                            <p className="compute-hero-subtitle">
                                Powerful virtual private servers for your projects
                            </p>
                            <p className="compute-hero-description">
                                Get dedicated resources with our high-performance VPS solutions. Perfect for SaaS applications, online stores, and production environments.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Pricing Plans Section */}
                <section className="compute-pricing">
                    <div className="container">
                        <div className="compute-section-header">
                            <h2>Choose Your Compute Plan</h2>
                            <p>Scalable VPS solutions for every workload</p>
                        </div>
                        <div className="compute-pricing-grid">
                            {/* Core Plan */}
                            <div className="compute-pricing-card" id="core">
                                <div className="compute-pricing-header">
                                    <div className="compute-plan-badge core">Core</div>
                                    <h3>VPS-A Core Compute</h3>
                                    <p className="compute-pricing-tagline">Ideal for multiple websites, small SaaS projects, or staging + production environments on the same VPS</p>
                                </div>
                                <div className="compute-pricing-price">
                                    <span className="compute-price-label">Starting from</span>
                                    <span className="compute-price-amount">$64.90</span>
                                    <span className="compute-price-period">USD / Monthly</span>
                                </div>
                                <ul className="compute-pricing-features">
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>4 shared vCPUs</strong>
                                            <span className="compute-feature-label">CPU</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>8 GB RAM</strong>
                                            <span className="compute-feature-label">Memory</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>80 GB NVMe SSD</strong>
                                            <span className="compute-feature-label">Storage</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>20 TB / month</strong>
                                            <span className="compute-feature-label">Traffic</span>
                                        </div>
                                    </li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/compute/vps1"
                                    className="compute-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Order Now
                                </a>
                            </div>

                            {/* Edge Plan */}
                            <div className="compute-pricing-card featured" id="edge">
                                <div className="compute-pricing-badge-top">Popular Choice</div>
                                <div className="compute-pricing-header">
                                    <div className="compute-plan-badge edge">Edge</div>
                                    <h3>VPS-B Edge Compute</h3>
                                    <p className="compute-pricing-tagline">Built for growing online stores, heavier SaaS workloads, or multiple containers and services running in parallel</p>
                                </div>
                                <div className="compute-pricing-price">
                                    <span className="compute-price-label">Starting from</span>
                                    <span className="compute-price-amount">$168.90</span>
                                    <span className="compute-price-period">USD / Monthly</span>
                                </div>
                                <ul className="compute-pricing-features">
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>8 shared vCPUs</strong>
                                            <span className="compute-feature-label">CPU</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>16 GB RAM</strong>
                                            <span className="compute-feature-label">Memory</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>160 GB SSD</strong>
                                            <span className="compute-feature-label">Storage</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>20 TB / month</strong>
                                            <span className="compute-feature-label">Traffic</span>
                                        </div>
                                    </li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/compute/vm-edge"
                                    className="compute-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Order Now
                                </a>
                            </div>

                            {/* MTI-L Plan */}
                            <div className="compute-pricing-card" id="mti-l">
                                <div className="compute-pricing-header">
                                    <div className="compute-plan-badge mti">MTI-L</div>
                                    <h3>VPS-C MTI-L Compute</h3>
                                    <p className="compute-pricing-tagline">Designed for heavy production workloads, multiple client projects, or resource-hungry applications that need plenty of RAM and CPU</p>
                                </div>
                                <div className="compute-pricing-price">
                                    <span className="compute-price-label">Starting from</span>
                                    <span className="compute-price-amount">$269.90</span>
                                    <span className="compute-price-period">USD / Monthly</span>
                                </div>
                                <ul className="compute-pricing-features">
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>16 shared vCPUs</strong>
                                            <span className="compute-feature-label">CPU</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>35 GB RAM</strong>
                                            <span className="compute-feature-label">Memory</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>320 GB SSD</strong>
                                            <span className="compute-feature-label">Storage</span>
                                        </div>
                                    </li>
                                    <li>
                                        <div className="compute-feature-item">
                                            <strong>20 TB / month</strong>
                                            <span className="compute-feature-label">Traffic</span>
                                        </div>
                                    </li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/compute/mti-l"
                                    className="compute-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Order Now
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Managed Services Add-on */}
                <section className="compute-managed">
                    <div className="container">
                        <div className="compute-managed-content">
                            <div className="compute-managed-icon">
                                <i className="las la-shield-alt"></i>
                            </div>
                            <div className="compute-managed-text">
                                <h2>Full Management Available</h2>
                                <p>Add complete management to your VPS for additional peace of mind</p>
                            </div>
                        </div>
                        <div className="compute-managed-features">
                            <div className="compute-managed-feature">
                                <i className="las la-chart-area"></i>
                                <h4>Monitoring</h4>
                                <p>Continuous server monitoring and proactive alerts</p>
                            </div>
                            <div className="compute-managed-feature">
                                <i className="las la-lock"></i>
                                <h4>Security Management</h4>
                                <p>Regular security updates and vulnerability patching</p>
                            </div>
                            <div className="compute-managed-feature">
                                <i className="las la-database"></i>
                                <h4>Remote Backups</h4>
                                <p>7-day backup retention with easy restoration</p>
                            </div>
                            <div className="compute-managed-feature">
                                <i className="las la-headset"></i>
                                <h4>Technical Support</h4>
                                <p>Priority support from our expert team</p>
                            </div>
                        </div>
                        <div className="compute-managed-cta">
                            <p className="compute-managed-note">Available as an add-on for any VPS plan</p>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="compute-features">
                    <div className="container">
                        <div className="compute-section-header">
                            <h2>Why Choose Our VPS</h2>
                            <p>Enterprise-grade infrastructure for your applications</p>
                        </div>
                        <div className="compute-features-grid">
                            <div className="compute-feature-card">
                                <i className="las la-server"></i>
                                <h3>NVMe SSD Storage</h3>
                                <p>Lightning-fast NVMe drives for maximum performance</p>
                            </div>
                            <div className="compute-feature-card">
                                <i className="las la-network-wired"></i>
                                <h3>High Bandwidth</h3>
                                <p>Generous traffic allowances with high-speed networking</p>
                            </div>
                            <div className="compute-feature-card">
                                <i className="las la-expand-arrows-alt"></i>
                                <h3>Full Root Access</h3>
                                <p>Complete control over your server environment</p>
                            </div>
                            <div className="compute-feature-card">
                                <i className="las la-shield-alt"></i>
                                <h3>DDoS Protection</h3>
                                <p>Built-in protection against distributed attacks</p>
                            </div>
                            <div className="compute-feature-card">
                                <i className="las la-clock"></i>
                                <h3>99.9% Uptime</h3>
                                <p>Reliable infrastructure with guaranteed uptime</p>
                            </div>
                            <div className="compute-feature-card">
                                <i className="las la-cog"></i>
                                <h3>Easy Management</h3>
                                <p>Intuitive control panel for server management</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="compute-cta">
                    <div className="container">
                        <div className="compute-cta-content">
                            <h2>Ready to Deploy Your VPS?</h2>
                            <p>Get started with powerful cloud compute resources today</p>
                            <div className="compute-cta-buttons">
                                <a href="#core" className="compute-btn compute-btn-light">
                                    View Plans
                                </a>
                                <a href="/contact1" className="compute-btn compute-btn-outline">
                                    Contact Sales
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default Compute;

